import { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { auth, db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';

export const VIDEO_TYPE = 'video/mp4';
export const PPT_TYPES = ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];

/** Kind of an SOP file: pdf, video, ppt or other. */
export const sopKind = (fileType) => {
  if (fileType === 'application/pdf') return 'pdf';
  if (fileType === VIDEO_TYPE) return 'video';
  if (PPT_TYPES.includes(fileType)) return 'ppt';
  return 'other';
};

/** Documents of a collection assigned to a role: its own plus the ones for 'Hepsi'. */
const fetchForRole = async (collectionName, role) => {
  const ref = collection(db, collectionName);
  const [roleSnapshot, hepsiSnapshot] = await Promise.all([
    getDocs(query(ref, where('group', 'array-contains', role))),
    getDocs(query(ref, where('group', 'array-contains', 'Hepsi'))),
  ]);
  const map = new Map();
  roleSnapshot.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
  hepsiSnapshot.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
  return map;
};

/**
 * SOPs, forms and indication trainings assigned to the user's role, with what
 * they have opened, confirmed as read or watched. Also owns the viewers
 * opened from the Files tab (SOP viewer, video SOP module, training player)
 * and the end-of-reading quiz.
 */
const useDocumentLibrary = (role) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const [mainSOPList, setMainSOPList] = useState([]);
  const [formsList, setFormsList] = useState([]);
  const [indicationEducations, setIndicationEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewedSOPs, setViewedSOPs] = useState(new Set());
  const [readSOPs, setReadSOPs] = useState(new Set()); // "Okudum" onayları
  const [viewedForms, setViewedForms] = useState(new Set());
  const [viewedIndicationEducations, setViewedIndicationEducations] = useState(new Set());

  // Viewers
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [sopQuizTarget, setSopQuizTarget] = useState(null);
  const [selectedEducationSOP, setSelectedEducationSOP] = useState(null);
  const [showEducationModule, setShowEducationModule] = useState(false);
  const [selectedIndicationEducation, setSelectedIndicationEducation] = useState(null);
  const [showEducationPlayer, setShowEducationPlayer] = useState(false);

  const fetchViewedSOPs = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const querySnapshot = await getDocs(query(collection(db, 'userSOPViews'), where('userId', '==', user.uid)));
      setViewedSOPs(new Set(querySnapshot.docs.map((d) => d.data().sopId)));
    } catch (error) {
      console.error('Error fetching viewed SOPs:', error);
    }
  };

  const fetchViewedForms = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const querySnapshot = await getDocs(query(collection(db, 'userFormViews'), where('userId', '==', user.uid)));
      setViewedForms(new Set(querySnapshot.docs.map((d) => d.data().formId)));
    } catch (error) {
      console.error('Error fetching viewed Forms:', error);
    }
  };

  const fetchReadSOPs = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const querySnapshot = await getDocs(query(collection(db, 'userSOPReadConfirmations'), where('userId', '==', user.uid)));
      // Geri alınmış (isRead: false) onaylar okunmamış sayılır; eski kayıtlarda alan yok → okunmuş
      setReadSOPs(new Set(querySnapshot.docs.filter((d) => d.data().isRead !== false).map((d) => d.data().sopId)));
    } catch (error) {
      console.error('Error fetching read SOPs:', error);
    }
  };

  // İzlenme oranı %50'yi geçen eğitimler tamamlanmış sayılır
  const fetchViewedIndicationEducations = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const querySnapshot = await getDocs(query(collection(db, 'userEducationProgress'), where('userId', '==', user.uid)));
      const viewedIds = new Set();
      querySnapshot.docs.forEach((d) => {
        const data = d.data();
        if (data.watchedProgress >= 50) viewedIds.add(data.educationId);
      });
      setViewedIndicationEducations(viewedIds);
    } catch (error) {
      console.error('Error fetching viewed indication educations:', error);
    }
  };

  // Rol + 'Hepsi' grubu içerikleri tek seferde, paralel yüklenir
  useEffect(() => {
    if (!role) return;

    fetchViewedSOPs();
    fetchReadSOPs();
    fetchViewedForms();
    fetchViewedIndicationEducations();

    const loadAll = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchForRole('SOPs', role).then((map) => setMainSOPList(Array.from(map.values()))),
          fetchForRole('Forms', role).then((map) => setFormsList(Array.from(map.values()))),
          // Yalnızca video adresi olan (yeni biçim) eğitimler
          fetchForRole('IndicationEducations', role)
            .then((map) => setIndicationEducations(Array.from(map.values()).filter((education) => education.videoUrl)))
            .catch((error) => console.error('Error loading indication educations:', error)),
        ]);
      } catch (error) {
        console.error('Error loading document data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const markSOPAsViewed = async (sopId) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await addDoc(collection(db, 'userSOPViews'), { userId: user.uid, sopId, viewedAt: new Date() });
      setViewedSOPs((prev) => new Set([...prev, sopId]));
    } catch (error) {
      console.error('Error marking SOP as viewed:', error);
    }
  };

  const markFormAsViewed = async (formId) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await addDoc(collection(db, 'userFormViews'), { userId: user.uid, formId, viewedAt: new Date() });
      setViewedForms((prev) => new Set([...prev, formId]));
    } catch (error) {
      console.error('Error marking Form as viewed:', error);
    }
  };

  // "Okudum" onayı ver / geri al
  const toggleSOPRead = async (sopId) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const readRef = collection(db, 'userSOPReadConfirmations');
      const querySnapshot = await getDocs(query(readRef, where('userId', '==', user.uid), where('sopId', '==', sopId)));
      if (readSOPs.has(sopId)) {
        for (const docSnap of querySnapshot.docs) {
          // eslint-disable-next-line no-await-in-loop
          await updateDoc(doc(db, 'userSOPReadConfirmations', docSnap.id), { isRead: false, unreadAt: new Date() });
        }
        setReadSOPs((prev) => {
          const next = new Set(prev);
          next.delete(sopId);
          return next;
        });
        toast.info(tRef.current('profilePage.files.toast.markedUnread'));
      } else {
        if (querySnapshot.empty) {
          await addDoc(readRef, { userId: user.uid, sopId, isRead: true, readAt: new Date() });
        } else {
          // Daha önce geri alınmış onayı yeniden okundu yap (mükerrer kayıt açma)
          for (const docSnap of querySnapshot.docs) {
            // eslint-disable-next-line no-await-in-loop
            await updateDoc(doc(db, 'userSOPReadConfirmations', docSnap.id), { isRead: true, readAt: new Date() });
          }
        }
        setReadSOPs((prev) => new Set([...prev, sopId]));
        toast.success(tRef.current('profilePage.files.toast.markedRead'));
      }
    } catch (error) {
      console.error('Error toggling SOP read status:', error);
      toast.error(tRef.current('profilePage.files.toast.actionError'));
    }
  };

  const openSOP = (sop) => {
    // Yeniyse görüntülenmiş olarak işaretle
    if (sop.newlyAdded && !viewedSOPs.has(sop.id)) markSOPAsViewed(sop.id);

    // Video içerik Eğitim Modülünde, diğerleri doküman görüntüleyicide açılır
    if (sop.fileType === VIDEO_TYPE) {
      setSelectedEducationSOP(sop);
      setShowEducationModule(true);
    } else {
      setHasScrolledToBottom(false);
      setSelectedSOP(sop);
    }
  };

  const closeSOP = () => {
    setSelectedSOP(null);
    setHasScrolledToBottom(false);
  };

  const openForm = (form) => {
    if (form.newlyAdded && !viewedForms.has(form.id)) markFormAsViewed(form.id);
    if (form.fileUrl) {
      window.open(form.fileUrl, '_blank');
    } else {
      toast.info(tRef.current('profilePage.files.toast.noFormFile'));
    }
  };

  // Video SOP'de sınav oynatıcının içinde geçilir; okundu onayı burada verilir.
  // Aksi halde SOP bekleyen atamalarda kalır ve giriş hatırlatması tekrarlanır.
  const handleEducationComplete = async (sopId) => {
    if (!viewedSOPs.has(sopId)) markSOPAsViewed(sopId);
    if (!readSOPs.has(sopId)) {
      await toggleSOPRead(sopId);
    } else {
      toast.success(tRef.current('profilePage.files.toast.trainingDone'));
    }
  };

  const openIndicationEducation = (education) => {
    setSelectedIndicationEducation(education);
    setShowEducationPlayer(true);
  };

  const handleIndicationEducationComplete = async (educationId) => {
    setViewedIndicationEducations((prev) => new Set([...prev, educationId]));
    toast.success(tRef.current('profilePage.files.toast.trainingDone'));
  };

  const counts = useMemo(() => ({
    newSops: mainSOPList.filter((sop) => sop.newlyAdded && !viewedSOPs.has(sop.id)).length,
    newForms: formsList.filter((form) => form.newlyAdded && !viewedForms.has(form.id)).length,
    newEducations: indicationEducations.filter((education) => education.newlyAdded && !viewedIndicationEducations.has(education.id)).length,
    readSops: mainSOPList.filter((sop) => readSOPs.has(sop.id)).length,
    unreadSops: mainSOPList.filter((sop) => !readSOPs.has(sop.id)).length,
    watchedEducations: indicationEducations.filter((education) => viewedIndicationEducations.has(education.id)).length,
  }), [mainSOPList, formsList, indicationEducations, viewedSOPs, viewedForms, readSOPs, viewedIndicationEducations]);

  return {
    loading,
    mainSOPList,
    formsList,
    indicationEducations,
    viewedSOPs,
    readSOPs,
    viewedForms,
    viewedIndicationEducations,
    counts,
    toggleSOPRead,
    openSOP,
    closeSOP,
    openForm,
    selectedSOP,
    hasScrolledToBottom,
    setHasScrolledToBottom,
    sopQuizTarget,
    setSopQuizTarget,
    selectedEducationSOP,
    showEducationModule,
    setShowEducationModule,
    handleEducationComplete,
    selectedIndicationEducation,
    showEducationPlayer,
    openIndicationEducation,
    closeIndicationEducation: () => {
      setShowEducationPlayer(false);
      setSelectedIndicationEducation(null);
    },
    handleIndicationEducationComplete,
  };
};

export default useDocumentLibrary;
